import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getUiLang } from '../utils/lang';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fallbackImg = 'https://customer-assets.emergentagent.com/job_welcome-app-440/artifacts/1g8bb7ag_Sunify%20logo%20ok..jpg';

const CrossSell = ({ productId }) => {
  const { t, i18n } = useTranslation();
  const { currency } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/products/${productId}/cross-sell`);
        if (!res.ok) {
          if (!cancelled) setProducts([]);
          return;
        }
        const data = await res.json();
        if (!cancelled) setProducts(data.products || []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (productId) load();
    return () => { cancelled = true; };
  }, [productId]);

  if (loading || products.length === 0) return null;

  const lang = getUiLang(i18n);
  const getName = (p) =>
    p.translations?.[lang]?.name ||
    p.translations?.sk?.name ||
    p.translations?.en?.name ||
    Object.values(p.translations || {})[0]?.name ||
    '';

  const formatPrice = (p) => {
    const price = currency === 'EUR' ? p.price_eur : p.price_usd;
    const symbol = currency === 'EUR' ? '€' : '$';
    return `${Number(price).toFixed(2)} ${symbol}${p.unit === 'mb' ? '/mb' : ''}`;
  };

  return (
    <section className="border-t border-gray-200 p-8" data-testid="cross-sell-section">
      <div className="flex items-center gap-3 mb-6">
        <Package className="h-6 w-6 text-orange-500" />
        <h2 className="text-2xl font-bold">{t('product.cross_sell_title', 'Často kupované spolu')}</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {products.map((p) => (
          <Link
            key={p.id}
            to={`/product/${p.id}`}
            className="group bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-orange-400 hover:shadow-md transition"
            data-testid="cross-sell-card"
          >
            <div className="aspect-square bg-gray-100 overflow-hidden">
              <img
                src={(p.images && p.images[0]) || fallbackImg}
                alt={getName(p)}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={(e) => { e.currentTarget.src = fallbackImg; }}
              />
            </div>
            <div className="p-3">
              <div className="text-sm font-medium text-gray-800 line-clamp-2 min-h-[2.5rem]">{getName(p)}</div>
              <div className="mt-1 text-orange-600 font-bold text-sm">{formatPrice(p)}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default CrossSell;
